import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiMail, FiPaperclip, FiSearch, FiX, FiCheck, FiRefreshCw, FiEyeOff, FiRotateCcw, FiSlash, FiEdit, FiCornerUpLeft, FiCornerUpRight, FiTrash2, FiFolder } from 'react-icons/fi';
import axios from 'axios';
import { API_URL, API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import EmailBody from '../components/EmailBody';
import EmailEditor from '../components/EmailEditor';

const escapeHtml = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const plainToHtml = (s) => escapeHtml(s).replace(/\n/g, '<br>');

const cfg = () => {
  const token = localStorage.getItem('authToken');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

// The recipient currently being typed (after the last comma), for a To field that holds
// multiple comma-separated addresses.
const lastToken = (s) => ((s || '').split(',').pop() || '').trim();

// Triage screen for email we couldn't tie to a job. Private to the mailbox owner.
export default function EmailInbox() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [note, setNote] = useState('');
  const [token, setToken] = useState('');
  const [ignores, setIgnores] = useState([]);
  const [showIgnores, setShowIgnores] = useState(false);
  const [tab, setTab] = useState('unmatched');   // 'unmatched' | 'matched' | 'sent'
  const [sent, setSent] = useState([]);
  const [matched, setMatched] = useState([]);
  const [search, setSearch] = useState('');      // what's typed in the search box
  const [query, setQuery] = useState('');        // debounced — what we actually ask the server for
  const [searching, setSearching] = useState(false);
  // compose
  const [composing, setComposing] = useState(false);
  const [cTo, setCTo] = useState('');
  const [cSubject, setCSubject] = useState('');
  const [cBody, setCBody] = useState('');          // plain-text fallback
  const [cBodyHtml, setCBodyHtml] = useState('');  // rich-text HTML (what's sent)
  const [cJob, setCJob] = useState(null);        // optional {id, title}
  const [cJobQuery, setCJobQuery] = useState('');
  const [cJobHits, setCJobHits] = useState([]);
  const [sending, setSending] = useState(false);
  const [toHits, setToHits] = useState([]);        // "To" autocomplete
  const [toPicked, setToPicked] = useState(false); // suppress suggestions right after choosing
  const [replyToId, setReplyToId] = useState(null); // JobEmail we're replying to (threading)
  const [cFiles, setCFiles] = useState([]);         // attachments to send with the compose
  const [searchParams, setSearchParams] = useSearchParams();

  // Open the composer prefilled when linked here with ?to= (e.g. the Directory email button).
  useEffect(() => {
    const to = searchParams.get('to');
    if (!to) return;
    setComposing(true);
    setReplyToId(null);
    setCTo(to);
    setToPicked(true);
    setToHits([]);
    setCSubject(searchParams.get('subject') || '');
    setCBody(''); setCBodyHtml(''); setCJob(null); setCFiles([]);
    // Clear the params so a later manual "New Email" starts clean.
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // job picker state (per email)
  const [pickFor, setPickFor] = useState(null);
  const [jobQuery, setJobQuery] = useState('');
  const [jobHits, setJobHits] = useState([]);

  // Search runs on the server, so it reaches the whole mailbox history — not just the
  // page of email already on screen.
  const qParam = query ? `q=${encodeURIComponent(query)}` : '';

  const load = useCallback(async () => {
    const params = [showDismissed ? 'include_dismissed=1' : '', qParam].filter(Boolean).join('&');
    try {
      const res = await axios.get(`${API_URL}/emails/unmatched${params ? `?${params}` : ''}`, cfg());
      setRows(res.data || []);
    } catch (e) {
      setRows([]);
    }
  }, [showDismissed, qParam]);

  const loadIgnores = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/email-ignores`, cfg());
      setIgnores(res.data || []);
    } catch (e) { setIgnores([]); }
  }, []);

  const loadSent = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/emails/sent${qParam ? `?${qParam}` : ''}`, cfg());
      setSent(res.data || []);
    } catch (e) { setSent([]); }
  }, [qParam]);

  const loadMatched = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/emails/matched${qParam ? `?${qParam}` : ''}`, cfg());
      setMatched(res.data || []);
    } catch (e) { setMatched([]); }
  }, [qParam]);

  // Debounce typing so we're not firing a request per keystroke. One character is too
  // broad to be useful, so it takes two to start searching.
  useEffect(() => {
    const s = search.trim();
    const target = s.length >= 2 ? s : '';   // under 2 chars behaves like no search
    if (target === query) { setSearching(false); return undefined; }
    setSearching(true);
    const t = setTimeout(() => setQuery(target), 300);
    return () => clearTimeout(t);
  }, [search, query]);
  useEffect(() => { setSearching(false); }, [rows, matched, sent]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadIgnores(); }, [loadIgnores]);
  useEffect(() => { if (tab === 'sent') loadSent(); }, [tab, loadSent]);
  useEffect(() => { if (tab === 'matched') loadMatched(); }, [tab, loadMatched]);
  useEffect(() => { setToken(localStorage.getItem('authToken') || ''); }, []);

  // "To" autocomplete — clients, job contacts, teammates, recent correspondents. Only the
  // recipient being typed (after the last comma) is looked up, so you can add several.
  useEffect(() => {
    const tok = lastToken(cTo);
    if (toPicked || tok.length < 2) { setToHits([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/email-contacts?q=${encodeURIComponent(tok)}`, cfg());
        setToHits(res.data || []);
      } catch (e) { setToHits([]); }
    }, 220);
    return () => clearTimeout(t);
  }, [cTo, toPicked]);

  // Optional job link on a composed email — reuses the global search.
  useEffect(() => {
    if (cJobQuery.trim().length < 2) { setCJobHits([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/search?q=${encodeURIComponent(cJobQuery.trim())}`, cfg());
        const jobs = (res.data.groups || []).find(g => g.type === 'jobs');
        setCJobHits(jobs ? jobs.items : []);
      } catch (e) { setCJobHits([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [cJobQuery]);

  // Everyone on the original except us — the sender plus the other To/Cc recipients.
  // (The importer stores To and Cc together in to_addr, so this covers CC'd people.)
  const replyAllRecipients = (em) => {
    const mine = new Set([(user?.email || '').toLowerCase(), (em.mailbox || '').toLowerCase()].filter(Boolean));
    const others = (em.to_addr || '').split(',').map(s => s.trim()).filter(Boolean);
    const seen = new Set();
    const list = [];
    [em.from_addr, ...others].filter(Boolean).forEach(a => {
      const k = a.toLowerCase();
      if (mine.has(k) || seen.has(k)) return;
      seen.add(k);
      list.push(a);
    });
    return list;
  };

  // Reply to an email: prefill the composer at the top of the page.
  // all=true adds everyone else who was on it (reply-all).
  const replyTo = (em, all = false) => {
    const subj = (em.subject || '').trim();
    setCTo(all ? replyAllRecipients(em).join(', ') + ', ' : (em.from_addr || ''));
    setToPicked(true);
    setToHits([]);
    setCSubject(/^re:/i.test(subj) ? subj : `Re: ${subj}`);
    // Quote the original so the recipient has context, like a normal mail client.
    const when = em.received_at
      ? new Date(em.received_at + (em.received_at.endsWith('Z') ? '' : 'Z')).toLocaleString()
      : '';
    const quoted = (em.body_text || '').split('\n').map(l => '> ' + l).join('\n');
    const who = em.from_name || em.from_addr;
    setCBody(`\n\nOn ${when}, ${who} wrote:\n${quoted}`);
    setCBodyHtml(`<p></p><p>On ${escapeHtml(when)}, ${escapeHtml(who)} wrote:</p><blockquote>${plainToHtml(em.body_text || '')}</blockquote>`);
    setCJob(em.job_id ? { id: em.job_id, title: em.job_number || 'this job' } : null);
    setReplyToId(em.id);           // carries In-Reply-To so it threads for the recipient
    setCFiles([]);
    setComposing(true);
    setOpenId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalAttachBytes = cFiles.reduce((s, f) => s + (f.size || 0), 0);

  const addFiles = (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    setCFiles(prev => {
      // de-dupe by name+size so re-picking doesn't double-add
      const seen = new Set(prev.map(f => `${f.name}:${f.size}`));
      return [...prev, ...picked.filter(f => !seen.has(`${f.name}:${f.size}`))];
    });
  };
  const removeFile = (idx) => setCFiles(prev => prev.filter((_, i) => i !== idx));

  const sendCompose = async () => {
    if (!cTo.trim() || !cBody.trim()) return;
    if (totalAttachBytes > 20 * 1024 * 1024) { setNote('Attachments are too large (20 MB total max).'); return; }
    setSending(true); setNote('');
    try {
      const fd = new FormData();
      fd.append('to', cTo.trim());
      fd.append('subject', cSubject.trim());
      fd.append('body', cBody.trim());
      fd.append('body_html', cBodyHtml || '');
      if (cJob) fd.append('job_id', cJob.id);
      if (replyToId) fd.append('in_reply_to_id', replyToId);
      cFiles.forEach(f => fd.append('files', f));
      const res = await axios.post(`${API_URL}/emails/send`, fd, cfg());
      setNote(res.data?.message || 'Sent.');
      setComposing(false);
      setCTo(''); setCSubject(''); setCBody(''); setCBodyHtml(''); setCJob(null); setCJobQuery(''); setCJobHits([]); setReplyToId(null); setCFiles([]);
      if (tab === 'sent') loadSent();
    } catch (e) {
      setNote(e.response?.data?.error || 'Could not send.');
    } finally {
      setSending(false);
    }
  };

  // Job search reuses the global search endpoint.
  useEffect(() => {
    if (jobQuery.trim().length < 2) { setJobHits([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/search?q=${encodeURIComponent(jobQuery.trim())}`, cfg());
        const jobs = (res.data.groups || []).find(g => g.type === 'jobs');
        setJobHits(jobs ? jobs.items : []);
      } catch (e) { setJobHits([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [jobQuery]);

  const syncNow = async () => {
    setSyncing(true); setNote('');
    try {
      const res = await axios.post(`${API_URL}/emails/sync-now`, {}, cfg());
      const d = res.data || {};
      setNote(d.error ? d.error : `Checked mail — ${d.imported || 0} new (${d.matched || 0} filed to jobs, ${d.unmatched || 0} unmatched).`);
      await load();
    } catch (e) {
      setNote(e.response?.data?.error || 'Could not check mail.');
    } finally {
      setSyncing(false);
    }
  };

  const assign = async (emailId, link) => {
    const jobId = parseInt(String(link).split('/').pop(), 10);
    if (!jobId) return;
    try {
      await axios.post(`${API_URL}/emails/${emailId}/assign`, { job_id: jobId }, cfg());
      setPickFor(null); setJobQuery(''); setJobHits([]); setOpenId(null);
      await load();
      if (tab === 'matched') await loadMatched();
      setNote(tab === 'matched' ? 'Moved to the selected job.' : 'Filed to the job.');
    } catch (e) {
      setNote(e.response?.data?.error || 'Could not assign.');
    }
  };

  // Take an email off a job entirely (kept, just no longer tied to that job).
  const unassign = async (em) => {
    if (!window.confirm(`Remove this email from ${em.job_number}?\n\nThe email is kept — it just won't show on that job anymore.`)) return;
    try {
      await axios.post(`${API_URL}/emails/${em.id}/unassign`, {}, cfg());
      setOpenId(null);
      await load();
      if (tab === 'matched') await loadMatched();
      setNote('Removed from the job.');
    } catch (e) {
      setNote(e.response?.data?.error || 'Could not remove it from the job.');
    }
  };

  // Delete an email from the app AND move the inbox copy to Trash (recoverable).
  // Drop email(s) from every list immediately so the UI feels instant — the slow part
  // (moving the message to Trash on the mail server over IMAP) then runs in the background.
  const removeFromLists = (ids) => {
    const idSet = new Set(ids);
    setRows(prev => (prev || []).filter(e => !idSet.has(e.id)));
    setMatched(prev => prev.filter(e => !idSet.has(e.id)));
    setSent(prev => prev.filter(e => !idSet.has(e.id)));
  };
  const reloadCurrentTab = async () => {
    await load();
    if (tab === 'matched') loadMatched();
    if (tab === 'sent') loadSent();
  };

  const del = async (em) => {
    const touchesInbox = em.direction === 'in' && em.mailbox_owner_id === user?.id;
    const prompt = touchesInbox
      ? 'Delete this email?\n\nIt will also be moved to Trash in your inbox — recoverable there for ~30 days.'
      : 'Delete this email from the app?';
    if (!window.confirm(prompt)) return;
    removeFromLists([em.id]);   // optimistic — vanish now
    setOpenId(null);
    setNote('Deleting…');
    try {
      const res = await axios.delete(`${API_URL}/emails/${em.id}`, cfg());
      setNote(res.data?.inbox_result ? `Deleted. Inbox: ${res.data.inbox_result}.` : 'Deleted.');
    } catch (e) {
      setNote(e.response?.data?.error || 'Could not delete — restoring.');
      await reloadCurrentTab();   // put it back if the server refused
    }
  };

  // ----- Bulk select + delete -----
  const [selected, setSelected] = useState(() => new Set());
  const toggleSel = (id) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clearSel = () => setSelected(new Set());
  // Reset the selection whenever the tab changes (ids don't carry across lists).
  useEffect(() => { setSelected(new Set()); }, [tab]);
  const selCheckbox = (id) => (
    <input type="checkbox" checked={selected.has(id)} onChange={() => toggleSel(id)}
      onClick={(e) => e.stopPropagation()} title="Select for bulk delete"
      style={{ width: 16, height: 16, flexShrink: 0, cursor: 'pointer', margin: 0 }} />
  );
  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} email${ids.length > 1 ? 's' : ''}?\n\nInbox copies move to Trash (recoverable ~30 days).`)) return;
    clearSel(); setOpenId(null);
    removeFromLists(ids);   // optimistic — all vanish now
    setNote(`Deleting ${ids.length}…`);
    // One request for the whole batch. Firing N concurrent DELETEs meant N separate IMAP
    // logins, which was slow enough that a restart mid-batch failed most of them and the
    // emails came back on refresh.
    try {
      const res = await axios.post(`${API_URL}/emails/bulk-delete`, { ids }, cfg());
      const refused = res.data?.refused?.length || 0;
      setNote(`Deleted ${res.data?.deleted?.length ?? ids.length}` +
        (refused ? ` — ${refused} could not be deleted (not your mailbox)` : '') + '.');
      if (refused) await reloadCurrentTab();   // put back the ones the server refused
    } catch (e) {
      setNote(e.response?.data?.error || 'Could not delete — restoring.');
      await reloadCurrentTab();
    }
  };

  // ----- Forward an email (new thread; carries the original body + attachments) -----
  const forward = async (em) => {
    const subj = (em.subject || '').trim();
    setCTo(''); setToPicked(false); setToHits([]);
    setCSubject(/^fwd:/i.test(subj) ? subj : `Fwd: ${subj}`);
    const when = em.received_at
      ? new Date(em.received_at + (em.received_at.endsWith('Z') ? '' : 'Z')).toLocaleString()
      : '';
    const header =
      '\n\n---------- Forwarded message ----------\n' +
      `From: ${em.from_name || ''}${em.from_addr ? ` <${em.from_addr}>` : ''}\n` +
      `Date: ${when}\n` +
      `Subject: ${em.subject || ''}\n` +
      `To: ${em.to_addr || ''}\n\n`;
    setCBody(header + (em.body_text || ''));
    setCBodyHtml(`<p></p><p>---------- Forwarded message ----------<br>From: ${escapeHtml((em.from_name || '') + (em.from_addr ? ` <${em.from_addr}>` : ''))}<br>Date: ${escapeHtml(when)}<br>Subject: ${escapeHtml(em.subject || '')}<br>To: ${escapeHtml(em.to_addr || '')}</p><blockquote>${plainToHtml(em.body_text || '')}</blockquote>`);
    setCJob(em.job_id ? { id: em.job_id, title: em.job_number || 'this job' } : null);
    setReplyToId(null);   // a forward is a new message, not a reply
    setCFiles([]);
    setComposing(true);
    setOpenId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Re-fetch the original attachments and carry them along on the forward.
    if (em.attachments?.length) {
      setNote('Attaching original files…');
      try {
        const files = [];
        for (const a of em.attachments) {
          const resp = await axios.get(`${API_BASE_URL}${a.download_url}`, { ...cfg(), responseType: 'blob' });
          files.push(new File([resp.data], a.name || 'attachment', { type: resp.data.type || 'application/octet-stream' }));
        }
        setCFiles(files);
        setNote('');
      } catch (e) {
        setNote('Could not attach some of the original files — you can re-add them manually.');
      }
    }
  };

  const dismiss = async (emailId, opts = {}) => {
    try {
      const res = await axios.post(`${API_URL}/emails/${emailId}/dismiss`, opts, cfg());
      if (res.data?.ignored_pattern) {
        setNote(`Dismissed — and muting ${res.data.ignored_pattern}. Their mail won't show up here again.`);
        loadIgnores();
      }
      await load();
    } catch (e) {
      setNote(e.response?.data?.error || 'Could not update.');
    }
  };

  const unmute = async (ruleId) => {
    try {
      await axios.delete(`${API_URL}/email-ignores/${ruleId}`, cfg());
      loadIgnores();
      setNote('Un-muted — their future mail will show up here again.');
    } catch (e) {
      setNote('Could not un-mute.');
    }
  };

  const tabLabel = (t) => (t === 'unmatched' ? 'Needs filing' : t === 'matched' ? 'Filed to jobs' : 'Sent');
  const resultCount = tab === 'matched' ? matched.length : tab === 'sent' ? sent.length : (rows || []).length;

  const btn = (bg, color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 7,
    border: '1px solid #e2e8f0', background: bg, color, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
  });

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <h2 style={{ margin: 0, fontSize: 22, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiMail /> Email
        </h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button style={btn('#14532d', 'white')} onClick={() => {
            // Fresh compose — never inherit a previous reply's threading/prefill.
            setComposing(true); setReplyToId(null);
            setCTo(''); setCSubject(''); setCBody(''); setCBodyHtml(''); setCJob(null); setToPicked(false); setCFiles([]);
          }}>
            <FiEdit size={13} /> New Email
          </button>
          <button style={btn('white', '#334155')} onClick={syncNow} disabled={syncing}>
            <FiRefreshCw size={13} /> {syncing ? 'Checking…' : 'Check mail now'}
          </button>
          <button style={btn(showDismissed ? '#eef2ff' : 'white', '#334155')} onClick={() => setShowDismissed(s => !s)}>
            {showDismissed ? 'Hide dismissed' : 'Show dismissed'}
          </button>
          {ignores.length > 0 && (
            <button style={btn(showIgnores ? '#fff7ed' : 'white', '#9a3412')} onClick={() => setShowIgnores(s => !s)}>
              <FiSlash size={12} /> Muted ({ignores.length})
            </button>
          )}
        </div>
      </div>

      {showIgnores && ignores.length > 0 && (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 12.5, color: '#9a3412', fontWeight: 600, marginBottom: 8 }}>
            Muted senders — their mail never reaches this list
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {ignores.map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <code style={{ background: 'white', padding: '2px 6px', borderRadius: 4, border: '1px solid #fed7aa' }}>{r.pattern}</code>
                {r.note && <span style={{ color: '#9a3412', fontSize: 12 }}>{r.note}</span>}
                <button onClick={() => unmute(r.id)}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#9a3412', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}>
                  un-mute
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280' }}>
        {tab === 'unmatched'
          ? "Email from your mailbox we couldn't tie to a job. Only you can see this list. File it to a job, or dismiss it."
          : tab === 'matched'
            ? "Incoming client email that's been filed to a job. Open one to reply, or jump to its job."
            : "Emails you've sent from the app. Your signature is added automatically."}
      </p>

      {/* Compose — email anyone, job optional */}
      {composing && (
        <div style={{ background: 'white', border: '1px solid #bbf7d0', borderRadius: 12, padding: 14, marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 8 }}>New email</div>
          <div style={{ position: 'relative', marginBottom: 6 }}>
            <input
              value={cTo}
              onChange={(e) => { setCTo(e.target.value); setToPicked(false); }}
              placeholder="To — type a name or email (add more, separated by commas)"
              autoComplete="off"
              style={{ width: '100%', padding: 9, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14, boxSizing: 'border-box' }}
            />
            {toHits.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'white',
                border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 3, boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                maxHeight: 220, overflowY: 'auto',
              }}>
                {toHits.map((h, i) => (
                  <button key={h.email + i} onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const prefix = cTo.split(',').slice(0, -1).map(s => s.trim()).filter(Boolean);
                      setCTo([...prefix, h.email].join(', ') + ', ');
                      setToPicked(true); setToHits([]);
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '7px 10px', background: 'none', border: 'none', borderTop: i ? '1px solid #f1f5f9' : 'none', cursor: 'pointer', fontSize: 13 }}>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ fontWeight: 600 }}>{h.name}</span>
                      <span style={{ color: '#64748b' }}> · {h.email}</span>
                      {h.detail ? <span style={{ display: 'block', color: '#94a3b8', fontSize: 11.5 }}>{h.detail}</span> : null}
                    </span>
                    <span style={{
                      flexShrink: 0, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
                      padding: '2px 6px', borderRadius: 6,
                      background: h.kind === 'Client' ? '#dcfce7' : h.kind === 'Contact' ? '#dbeafe' : h.kind === 'Team' ? '#f1f5f9' : '#fef3c7',
                      color: h.kind === 'Client' ? '#166534' : h.kind === 'Contact' ? '#1d4ed8' : h.kind === 'Team' ? '#475569' : '#92400e',
                    }}>{h.kind}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <input value={cSubject} onChange={(e) => setCSubject(e.target.value)} placeholder="Subject"
            style={{ width: '100%', padding: 9, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14, marginBottom: 6, boxSizing: 'border-box' }} />
          <EmailEditor
            valueHtml={cBodyHtml}
            onChange={(html, text) => { setCBodyHtml(html); setCBody(text); }}
            placeholder="Type your message… (your signature is added automatically)"
            minHeight={180}
          />

          {/* optional job link */}
          <div style={{ marginTop: 8 }}>
            {cJob ? (
              <div style={{ fontSize: 12.5, color: '#166534', background: '#dcfce7', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 6 }}>
                Filed to {cJob.title}
                <button onClick={() => { setCJob(null); setCJobQuery(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#166534', display: 'flex' }}><FiX size={12} /></button>
              </div>
            ) : (
              <>
                <div style={{ position: 'relative' }}>
                  <FiSearch size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input value={cJobQuery} onChange={(e) => setCJobQuery(e.target.value)}
                    placeholder="Attach to a job (optional) — search number or address"
                    style={{ width: '100%', padding: '7px 8px 7px 26px', borderRadius: 6, border: '1px dashed #cbd5e1', fontSize: 12.5, boxSizing: 'border-box' }} />
                </div>
                {cJobHits.length > 0 && (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, marginTop: 4, maxHeight: 140, overflowY: 'auto', background: 'white' }}>
                    {cJobHits.map((h, i) => (
                      <button key={i} onClick={() => { setCJob({ id: parseInt(String(h.link).split('/').pop(), 10), title: h.title }); setCJobHits([]); }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 8px', background: 'none', border: 'none', borderTop: i ? '1px solid #f1f5f9' : 'none', cursor: 'pointer', fontSize: 12.5 }}>
                        <strong>{h.title}</strong>{h.subtitle ? <span style={{ color: '#64748b' }}> — {h.subtitle}</span> : null}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Attachments */}
          <div style={{ marginTop: 10 }}>
            <label style={{ ...btn('white', '#334155'), display: 'inline-flex' }}>
              <FiPaperclip size={13} /> Attach files
              <input type="file" multiple onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} style={{ display: 'none' }} />
            </label>
            {cFiles.length > 0 && (
              <span style={{ fontSize: 12, color: totalAttachBytes > 20 * 1024 * 1024 ? '#dc2626' : '#64748b', marginLeft: 8 }}>
                {cFiles.length} file{cFiles.length === 1 ? '' : 's'} · {(totalAttachBytes / (1024 * 1024)).toFixed(1)} MB{totalAttachBytes > 20 * 1024 * 1024 ? ' — over 20 MB limit' : ''}
              </span>
            )}
            {cFiles.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {cFiles.map((f, i) => (
                  <span key={`${f.name}:${f.size}:${i}`} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f1f5f9',
                    border: '1px solid #e2e8f0', borderRadius: 7, padding: '4px 8px', fontSize: 12, color: '#334155',
                  }}>
                    <FiPaperclip size={11} />
                    <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <span style={{ color: '#94a3b8' }}>{f.size < 1024 * 1024 ? `${(f.size / 1024).toFixed(0)} KB` : `${(f.size / (1024 * 1024)).toFixed(1)} MB`}</span>
                    <button onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', padding: 0 }}><FiX size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button style={btn('#14532d', 'white')} disabled={sending || !cTo.trim() || !cBody.trim()} onClick={sendCompose}>
              {sending ? 'Sending…' : 'Send'}
            </button>
            <button style={btn('white', '#334155')} onClick={() => { setComposing(false); setReplyToId(null); setCFiles([]); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {['unmatched', 'matched', 'sent'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{
              padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: '1px solid ' + (tab === t ? '#14532d' : '#e2e8f0'),
              background: tab === t ? '#14532d' : 'white',
              color: tab === t ? 'white' : '#475569',
            }}>
            {tabLabel(t)}
          </button>
        ))}
      </div>

      {/* Search — filters the tab you're on, across the whole mailbox history */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <FiSearch size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') { setSearch(''); setQuery(''); } }}
          placeholder={`Search ${tab === 'unmatched' ? 'email needing filing' : tab === 'matched' ? 'email filed to jobs' : 'sent email'} — sender, subject, body, job #`}
          style={{
            width: '100%', padding: '10px 34px 10px 34px', borderRadius: 9,
            border: '1px solid ' + (query ? '#14532d' : '#cbd5e1'), fontSize: 14, boxSizing: 'border-box',
          }}
        />
        {search && (
          <button onClick={() => { setSearch(''); setQuery(''); }} title="Clear search"
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}>
            <FiX size={15} />
          </button>
        )}
      </div>

      {query && (
        <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10 }}>
          {searching ? 'Searching…' : `${resultCount} result${resultCount === 1 ? '' : 's'} for “${query}” in ${tabLabel(tab)}`}
          {!searching && resultCount === 0 && (
            <span> — try the other tabs{tab === 'unmatched' && !showDismissed ? ', or “Show dismissed”' : ''}.</span>
          )}
        </div>
      )}

      {selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#991b1b' }}>{selected.size} selected</span>
          <button style={btn('#dc2626', 'white')} onClick={bulkDelete}><FiTrash2 size={12} /> Delete selected</button>
          <button style={btn('white', '#334155')} onClick={clearSel}>Clear</button>
        </div>
      )}

      {note && <div style={{ background: '#f1f5f9', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>{note}</div>}

      {tab === 'matched' ? (
        matched.length === 0 ? (
          <div style={{ background: 'white', borderRadius: 12, padding: 30, textAlign: 'center', color: '#6b7280', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            {query ? `No filed email matches “${query}”.` : 'No client email has been filed to a job yet.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {matched.map(em => (
              <div key={em.id} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                <button onClick={() => setOpenId(openId === em.id ? null : em.id)}
                  style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', textAlign: 'left', padding: '11px 13px', background: 'none', border: 'none', cursor: 'pointer' }}>
                  {selCheckbox(em.id)}
                  <span style={{
                    flexShrink: 0, fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#166534',
                    padding: '3px 8px', borderRadius: 6, maxWidth: 170, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{em.job_number}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{em.subject || '(no subject)'}</span>
                    <span style={{ display: 'block', color: '#64748b', fontSize: 12.5, marginTop: 2 }}>
                      from {em.from_name || em.from_addr}
                    </span>
                  </span>
                  {em.attachments?.length > 0 && (
                    <span style={{ fontSize: 11, color: '#64748b' }}><FiPaperclip size={11} /> {em.attachments.length}</span>
                  )}
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    {em.received_at ? new Date(em.received_at + (em.received_at.endsWith('Z') ? '' : 'Z')).toLocaleDateString() : ''}
                  </span>
                </button>
                {openId === em.id && (
                  <div style={{ borderTop: '1px solid #f1f5f9', padding: 13 }}>
                    <EmailBody text={em.body_text} html={em.body_html} style={{ fontSize: 13.5, color: '#0f172a', maxHeight: 260, overflowY: 'auto' }} />
                    {em.attachments?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                        {em.attachments.map(a => (
                          <a key={a.id} href={`${API_BASE_URL}${a.download_url}?token=${token}`}
                            style={{ fontSize: 12, background: '#eef2ff', color: '#3730a3', padding: '4px 8px', borderRadius: 6, textDecoration: 'none' }}>
                            <FiPaperclip size={10} /> {a.name}
                          </a>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                      <button style={btn('#0f766e', 'white')} onClick={() => replyTo(em)}>
                        <FiCornerUpLeft size={12} /> Reply
                      </button>
                      {replyAllRecipients(em).length > 1 && (
                        <button style={btn('white', '#0f766e')} title="Reply to everyone on this email"
                          onClick={() => replyTo(em, true)}>
                          <FiCornerUpLeft size={12} /> Reply All ({replyAllRecipients(em).length})
                        </button>
                      )}
                      <button style={btn('white', '#0f766e')} title="Forward this email to someone else" onClick={() => forward(em)}>
                        <FiCornerUpRight size={12} /> Forward
                      </button>
                      <button style={btn('white', '#334155')} onClick={() => navigate(`/jobs/${em.job_id}`)}>
                        Open {em.job_number}
                      </button>
                      <button style={btn('white', '#334155')} title="Filed to the wrong job? Move it to the correct one."
                        onClick={() => { setPickFor(pickFor === em.id ? null : em.id); setJobQuery(''); setJobHits([]); }}>
                        <FiFolder size={12} /> Move to another job
                      </button>
                      <button style={btn('white', '#334155')} title="Take it off this job — the email is kept"
                        onClick={() => unassign(em)}>
                        <FiSlash size={12} /> Remove from job
                      </button>
                      {em.mailbox_owner_id === user?.id && (
                        <button style={btn('#fee2e2', '#991b1b')} title="Delete + move to Trash in your inbox" onClick={() => del(em)}>
                          <FiTrash2 size={12} /> Delete
                        </button>
                      )}
                    </div>
                    {pickFor === em.id && (
                      <div style={{ marginTop: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>
                          Currently filed to <strong>{em.job_number}</strong>. Pick the correct job:
                        </div>
                        <div style={{ position: 'relative', marginBottom: 6 }}>
                          <FiSearch size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                          <input autoFocus value={jobQuery} onChange={(e) => setJobQuery(e.target.value)}
                            placeholder="Search job number or address…"
                            style={{ width: '100%', padding: '8px 8px 8px 28px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13.5, boxSizing: 'border-box' }} />
                          <button onClick={() => setPickFor(null)} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                            <FiX size={13} />
                          </button>
                        </div>
                        {jobHits.length > 0 ? (
                          <div style={{ maxHeight: 190, overflowY: 'auto' }}>
                            {jobHits.filter(h => parseInt(String(h.link).split('/').pop(), 10) !== em.job_id).map((h, i) => (
                              <button key={i} onClick={() => assign(em.id, h.link)}
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 8px', background: 'none', border: 'none', borderTop: i ? '1px solid #eef2f7' : 'none', cursor: 'pointer', fontSize: 13 }}>
                                <strong>{h.title}</strong>
                                {h.subtitle ? <span style={{ color: '#64748b' }}> — {h.subtitle}</span> : null}
                              </button>
                            ))}
                          </div>
                        ) : jobQuery.trim().length >= 2 ? (
                          <div style={{ fontSize: 12.5, color: '#94a3b8', padding: 4 }}>No jobs match “{jobQuery}”.</div>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : tab === 'sent' ? (
        sent.length === 0 ? (
          <div style={{ background: 'white', borderRadius: 12, padding: 30, textAlign: 'center', color: '#6b7280', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            {query ? `No sent email matches “${query}”.` : "You haven't sent any email from the app yet."}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sent.map(em => (
              <div key={em.id} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                <button onClick={() => setOpenId(openId === em.id ? null : em.id)}
                  style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', textAlign: 'left', padding: '11px 13px', background: 'none', border: 'none', cursor: 'pointer' }}>
                  {selCheckbox(em.id)}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{em.subject || '(no subject)'}</span>
                    <span style={{ display: 'block', color: '#64748b', fontSize: 12.5, marginTop: 2 }}>
                      to {em.to_addr}
                      {em.job_number && <span style={{ color: '#166534' }}> · filed to {em.job_number}</span>}
                    </span>
                  </span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    {em.received_at ? new Date(em.received_at + (em.received_at.endsWith('Z') ? '' : 'Z')).toLocaleDateString() : ''}
                  </span>
                </button>
                {openId === em.id && (
                  <div style={{ borderTop: '1px solid #f1f5f9', padding: 13 }}>
                    <EmailBody text={em.body_text} html={em.body_html} style={{ fontSize: 13.5, color: '#0f172a' }} />
                    <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button style={btn('white', '#0f766e')} title="Forward this email to someone else" onClick={() => forward(em)}>
                        <FiCornerUpRight size={12} /> Forward
                      </button>
                      <button style={btn('#fee2e2', '#991b1b')} title="Delete this sent record from the app" onClick={() => del(em)}>
                        <FiTrash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : rows === null ? (
        <div style={{ color: '#6b7280' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ background: 'white', borderRadius: 12, padding: 30, textAlign: 'center', color: '#6b7280', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          {query
            ? `No email in this list matches “${query}”.`
            : <>Nothing to triage. {showDismissed ? '' : 'Anything we could match went straight to its job.'}</>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map(em => {
            const open = openId === em.id;
            const isDismissed = !!em.dismissed_at;
            return (
              <div key={em.id} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, opacity: isDismissed ? 0.6 : 1 }}>
                <button
                  onClick={() => setOpenId(open ? null : em.id)}
                  style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', textAlign: 'left', padding: '11px 13px', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {selCheckbox(em.id)}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{em.subject || '(no subject)'}</span>
                    <span style={{ display: 'block', color: '#64748b', fontSize: 12.5, marginTop: 2 }}>
                      {em.from_name ? `${em.from_name} · ` : ''}{em.from_addr}
                    </span>
                  </span>
                  {em.attachments?.length > 0 && (
                    <span style={{ fontSize: 11, color: '#64748b' }}><FiPaperclip size={11} /> {em.attachments.length}</span>
                  )}
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    {em.received_at ? new Date(em.received_at + (em.received_at.endsWith('Z') ? '' : 'Z')).toLocaleDateString() : ''}
                  </span>
                </button>

                {open && (
                  <div style={{ borderTop: '1px solid #f1f5f9', padding: 13 }}>
                    <EmailBody text={em.body_text} html={em.body_html} style={{ fontSize: 13.5, color: '#0f172a', maxHeight: 260, overflowY: 'auto', marginBottom: 10 }} />
                    {em.attachments?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                        {em.attachments.map(a => (
                          <a key={a.id} href={`${API_BASE_URL}${a.download_url}?token=${token}`}
                            style={{ fontSize: 12, background: '#eef2ff', color: '#3730a3', padding: '4px 8px', borderRadius: 6, textDecoration: 'none' }}>
                            <FiPaperclip size={10} /> {a.name}
                          </a>
                        ))}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      {isDismissed ? (
                        <button style={btn('white', '#334155')} onClick={() => dismiss(em.id, { undo: true })}>
                          <FiRotateCcw size={12} /> Restore
                        </button>
                      ) : (
                        <>
                          <button style={btn('#0f766e', 'white')} onClick={() => replyTo(em)}>
                            <FiCornerUpLeft size={12} /> Reply
                          </button>
                          {replyAllRecipients(em).length > 1 && (
                            <button style={btn('white', '#0f766e')} title="Reply to everyone on this email"
                              onClick={() => replyTo(em, true)}>
                              <FiCornerUpLeft size={12} /> Reply All ({replyAllRecipients(em).length})
                            </button>
                          )}
                          <button style={btn('white', '#0f766e')} title="Forward this email to someone else" onClick={() => forward(em)}>
                            <FiCornerUpRight size={12} /> Forward
                          </button>
                          <button style={btn('#14532d', 'white')} onClick={() => { setPickFor(pickFor === em.id ? null : em.id); setJobQuery(''); setJobHits([]); }}>
                            <FiCheck size={12} /> File to a job
                          </button>
                          <button style={btn('white', '#334155')} onClick={() => dismiss(em.id, {})}>
                            <FiEyeOff size={12} /> Dismiss
                          </button>
                          <button
                            style={btn('#fff7ed', '#9a3412')}
                            title={`Dismiss this and mute ${em.from_addr} — their future mail won't appear here`}
                            onClick={() => dismiss(em.id, { ignore_sender: true })}
                          >
                            <FiSlash size={12} /> Mute sender
                          </button>
                          {em.from_addr && em.from_addr.includes('@') && (
                            <button
                              style={btn('#fff7ed', '#9a3412')}
                              title={`Mute everything from @${em.from_addr.split('@').pop()}`}
                              onClick={() => {
                                if (window.confirm(`Mute ALL mail from @${em.from_addr.split('@').pop()}?`)) {
                                  dismiss(em.id, { ignore_sender: true, ignore_domain: true });
                                }
                              }}
                            >
                              Mute @{em.from_addr.split('@').pop()}
                            </button>
                          )}
                          <button style={btn('#fee2e2', '#991b1b')} title="Delete from the app + move to Trash in your inbox" onClick={() => del(em)}>
                            <FiTrash2 size={12} /> Delete
                          </button>
                        </>
                      )}
                    </div>

                    {pickFor === em.id && (
                      <div style={{ marginTop: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
                        <div style={{ position: 'relative', marginBottom: 6 }}>
                          <FiSearch size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                          <input
                            autoFocus
                            value={jobQuery}
                            onChange={(e) => setJobQuery(e.target.value)}
                            placeholder="Search job number or address…"
                            style={{ width: '100%', padding: '8px 8px 8px 28px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13.5, boxSizing: 'border-box' }}
                          />
                          <button onClick={() => setPickFor(null)} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                            <FiX size={13} />
                          </button>
                        </div>
                        {jobHits.length > 0 ? (
                          <div style={{ maxHeight: 190, overflowY: 'auto' }}>
                            {jobHits.map((h, i) => (
                              <button key={i} onClick={() => assign(em.id, h.link)}
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 8px', background: 'none', border: 'none', borderTop: i ? '1px solid #eef2f7' : 'none', cursor: 'pointer', fontSize: 13 }}>
                                <strong>{h.title}</strong>
                                {h.subtitle ? <span style={{ color: '#64748b' }}> — {h.subtitle}</span> : null}
                              </button>
                            ))}
                          </div>
                        ) : jobQuery.trim().length >= 2 ? (
                          <div style={{ fontSize: 12.5, color: '#94a3b8', padding: 4 }}>No jobs match “{jobQuery}”.</div>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
